import { apiClient } from "../../api/client";
import type { ApiResponse } from "../../api/response";
import type { Subscription } from "../models";
import type { SaveSubscriptionInput } from "../types";
import {
  getStoreSubscriptions,
  mutateSubscriptionStore,
  shouldUseSubscriptionApi,
} from "../data/store";
import { SUBSCRIPTION_API_ENDPOINTS } from "./endpoints";

const now = () => new Date().toISOString();

async function fetchApi<T>(path: string): Promise<T | null> {
  try {
    return await apiClient.get<T>(path);
  } catch {
    return null;
  }
}

export const subscriptionRepository = {
  async findAll(): Promise<Subscription[]> {
    if (shouldUseSubscriptionApi()) {
      const result = await fetchApi<ApiResponse<Subscription[]>>(
        SUBSCRIPTION_API_ENDPOINTS.subscriptions.list
      );
      if (result?.data) return result.data;
    }
    return getStoreSubscriptions();
  },

  async findById(id: string): Promise<Subscription | null> {
    if (shouldUseSubscriptionApi()) {
      const result = await fetchApi<ApiResponse<Subscription>>(
        SUBSCRIPTION_API_ENDPOINTS.subscriptions.byId(id)
      );
      if (result?.data) return result.data;
    }
    return getStoreSubscriptions().find((s) => s.id === id) ?? null;
  },

  async findByTenantId(tenantId: string): Promise<Subscription | null> {
    if (shouldUseSubscriptionApi()) {
      const result = await fetchApi<ApiResponse<Subscription>>(
        SUBSCRIPTION_API_ENDPOINTS.subscriptions.byTenant(tenantId)
      );
      if (result?.data) return result.data;
    }
    return (
      getStoreSubscriptions().find((s) => s.tenantId === tenantId) ?? null
    );
  },

  async save(input: SaveSubscriptionInput): Promise<Subscription> {
    if (shouldUseSubscriptionApi()) {
      const path = input.id
        ? SUBSCRIPTION_API_ENDPOINTS.subscriptions.byId(input.id)
        : SUBSCRIPTION_API_ENDPOINTS.subscriptions.list;
      const result = input.id
        ? await apiClient.patch<ApiResponse<Subscription>>(path, input)
        : await apiClient.post<ApiResponse<Subscription>>(path, input);
      return result.data;
    }

    const subscription: Subscription = {
      id: input.id ?? `sub-${input.tenantId}-${Date.now()}`,
      tenantId: input.tenantId,
      planId: input.planId,
      status: input.status,
      startDate: input.startDate,
      endDate: input.endDate,
      trialEndsAt: input.trialEndsAt,
      quotas: input.quotas,
      features: input.features,
      billingCycle: input.billingCycle,
      createdAt: now(),
      updatedAt: now(),
    };

    mutateSubscriptionStore((s) => {
      const idx = s.subscriptions.findIndex(
        (x) => x.id === subscription.id || x.tenantId === input.tenantId
      );
      if (idx >= 0) {
        s.subscriptions[idx] = {
          ...s.subscriptions[idx],
          ...subscription,
          createdAt: s.subscriptions[idx].createdAt,
          updatedAt: now(),
        };
      } else {
        s.subscriptions.push(subscription);
      }
    });

    return subscription;
  },
};
